import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TaskList from './components/task-list';
import TaskItem from './components/task-item';
import http from 'src/services/httpService';
import LoadingContainer from 'src/componentes/loading-container';
import errorHandler from '../../../utils/error-handler';

const obterStatusId = (item) => item?.status?.id;

const encontrarLista = (list, statusId) =>
  list?.find((ls) => ls?.id == statusId) ||
  list?.find((ls) =>
    ls?.itens?.some((item) => obterStatusId(item) == statusId)
  );

function Kanban({
  columnsKanban,
  columnsCard,
  filtrando,
  model,
  parameters,
  listagemUnica = false,
  list: listProp,
  handleChangeFiltrandoBloqueado,
  handleDescricaoClick,
  isReadOnly = false,
}) {
  const [list, setList] = useState(listProp != null ? listProp : []);
  const [taskFantasma, setTaskFantasma] = useState(null);

  const statusAlteradoRef = useRef(new Map());
  const alterandoStatus = useRef(false);
  const [salvandoStatus, setSalvandoStatus] = useState(false);

  useEffect(() => {
    if (listProp != null) setList(listProp);
  }, [listProp]);

  useEffect(() => {
    statusAlteradoRef.current = new Map();
  }, [filtrando]);

  //Não é utilizado para listagem única
  const itensLista = useCallback(
    (statusId) =>
      encontrarLista(list, statusId)?.itens?.filter(
        (item) =>
          item && (item.status == null || obterStatusId(item) == statusId)
      ) || [],
    [list]
  );

  const getRealCount = useCallback(
    (statusId) => encontrarLista(list, statusId)?.qntRealItens || 0,
    [list]
  );

  //Não é utilizado para listagem única
  const alterarStatusAcao = useCallback((draggableId, novoStatusId, farol) => {
    setList((currentList) => {
      const newLists = currentList.map((item) => ({
        ...item,
        itens: item.itens ? [...item.itens] : item.itens,
      }));

      const listaOrigem = newLists.find((item) =>
        item.itens?.some((acao) => acao.id == draggableId)
      );
      const listaDestino = newLists.find((item) => item.id == novoStatusId);

      if (!listaOrigem || !listaDestino?.itens) return currentList;

      const index = listaOrigem.itens.findIndex(
        (acao) => acao.id == draggableId
      );

      const acao = {
        ...listaOrigem.itens[index],
        status: { id: novoStatusId },
        farolAcao: farol,
      };

      if (listaDestino === listaOrigem) listaOrigem.itens[index] = acao;
      else {
        listaOrigem.itens.splice(index, 1);
        listaOrigem.qntRealItens = Math.max(
          0,
          (listaOrigem.qntRealItens || 0) - 1
        );

        listaDestino.itens = [acao, ...listaDestino.itens];
        listaDestino.qntRealItens = (listaDestino.qntRealItens || 0) + 1;
      }

      return newLists;
    });
  }, []);

  //Utilizado para listagem única
  const removeAndAddListaUnica = useCallback(
    (draggableId, destinationId, farol = null) => {
      setList((currentList) => {
        const listaCopia = [...currentList];
        const acaoIndex = listaCopia.findIndex(
          (acao) => acao.id == draggableId
        );

        if (acaoIndex == -1) return currentList;

        const acao = {
          ...listaCopia[acaoIndex],
          status: { id: destinationId },
          farolAcao: farol,
        };

        if (listaCopia[acaoIndex].status?.id == destinationId)
          listaCopia[acaoIndex] = acao;
        else {
          listaCopia.splice(acaoIndex, 1);
          listaCopia.unshift(acao);
        }

        return listaCopia;
      });
    },
    []
  );

  //Sempre utilizado
  const callRemoveAndAdd = useCallback(
    (draggableId, destinationId, farol = null) => {
      if (listagemUnica) {
        removeAndAddListaUnica(draggableId, destinationId, farol);
        return;
      }

      statusAlteradoRef.current.set(String(draggableId), destinationId);
      alterarStatusAcao(draggableId, destinationId, farol);
      handleChangeFiltrandoBloqueado && handleChangeFiltrandoBloqueado();
    },
    [
      alterarStatusAcao,
      handleChangeFiltrandoBloqueado,
      listagemUnica,
      removeAndAddListaUnica,
    ]
  );

  //Sempre utilizado
  const handleDragStart = useCallback(
    (event) => {
      if (listagemUnica)
        setTaskFantasma(list.find((item) => item.id == event.draggableId));
      else {
        const source = event.source;
        setTaskFantasma(
          itensLista(source && source.droppableId).find(
            (item) => item.id == event.draggableId
          )
        );
      }
    },
    [itensLista, list, listagemUnica]
  );

  //Sempre utilizado
  const handleDragEnd = useCallback(
    async (event) => {
      setTaskFantasma(null);

      const { source, destination } = event;
      if (!destination || isReadOnly || alterandoStatus.current) return;

      const destinationId = parseInt(destination.droppableId, 10);
      const sourceId = parseInt(source.droppableId, 10);
      if (destinationId === sourceId) return;
      const acao = listagemUnica
        ? list.find((item) => item.id == event.draggableId)
        : itensLista(sourceId).find((item) => item.id == event.draggableId);
      if (!acao || acao.somenteLeitura) return;
      alterandoStatus.current = true;
      setSalvandoStatus(true);

      try {
        callRemoveAndAdd(event.draggableId, destinationId);
        const { data } = await http.post(`/Acao/AlterarApenasStatus`, {
          acaoId: event.draggableId,
          novoStatusId: destinationId,
        });
        callRemoveAndAdd(
          event.draggableId,
          data.status?.id ?? destinationId,
          data.farol
        );
      } catch (error) {
        callRemoveAndAdd(event.draggableId, sourceId, acao?.farolAcao);
        errorHandler(error);
      } finally {
        alterandoStatus.current = false;
        setSalvandoStatus(false);
      }
    },
    [callRemoveAndAdd, isReadOnly, itensLista, list, listagemUnica]
  );

  //Não é utilizado para listagem única
  const adicionarLista = useCallback((lista) => {
    setList((currentList) => {
      const listaCopia = [...currentList];
      const indexLista = listaCopia.findIndex((item) => item.id == lista.id);

      const statusAlterado = statusAlteradoRef.current;
      const itensRecebidos = (lista.itens || []).filter((item) => {
        const statusDrag = item && statusAlterado.get(String(item.id));
        return item && (statusDrag == null || statusDrag == lista.id);
      });

      const itensPendentes = (listaCopia[indexLista]?.itens || []).filter(
        (item) =>
          item &&
          statusAlterado.get(String(item.id)) == lista.id &&
          !itensRecebidos.some((recebido) => recebido.id == item.id)
      );

      const listaAjustada = {
        ...lista,
        itens: [...itensPendentes, ...itensRecebidos],
      };

      if (indexLista == -1) listaCopia.push(listaAjustada);
      else listaCopia[indexLista] = listaAjustada;

      return listaCopia.filter(
        (item, index, array) =>
          array.findIndex((currentItem) => currentItem.id === item.id) === index
      );
    });
  }, []);

  const columnsCardFinal = Object.assign([], columnsCard);

  return (
    <LoadingContainer
      isLoading={salvandoStatus}
      aria-busy={salvandoStatus}
      className="tw:flex tw:h-full tw:flex-col"
    >
      <div className="tw:h-[75vh] tw:flex-1 tw:overflow-x-auto tw:overflow-y-hidden tw:text-center tw:whitespace-nowrap">
        <DragDropContext
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {columnsKanban &&
            columnsKanban.map((column) => (
              <Droppable droppableId={column.id + ''} key={column.id}>
                {(provided, snapshot) => (
                  <TaskList
                    listagemUnica={listagemUnica}
                    adicionarLista={adicionarLista}
                    statusId={column && column.id}
                    filtrando={filtrando}
                    model={model}
                    parameters={parameters}
                    quantidadeColunas={columnsKanban && columnsKanban.length}
                    provided={provided}
                    snapshot={snapshot}
                    title={column.headerText}
                    total={
                      listagemUnica
                        ? list &&
                          list.filter((a) => a.status.id == column.id).length
                        : getRealCount(column.id)
                    }
                  >
                    {taskFantasma && (
                      <div
                        style={{
                          display: snapshot.isDraggingOver ? 'block' : 'none',
                        }}
                      >
                        <TaskItem
                          ehFantasma={true}
                          columnsCard={columnsCardFinal}
                          task={taskFantasma}
                        />
                      </div>
                    )}
                    {(listagemUnica
                      ? list && list.filter((a) => a.status.id === column.id)
                      : itensLista(column.id)
                    ).map((task, index) => (
                      <Draggable
                        draggableId={task.id + ''}
                        index={index}
                        key={task.id}
                        isDragDisabled={
                          isReadOnly ||
                          salvandoStatus ||
                          task.somenteLeitura === true
                        }
                      >
                        {(provided, snapshot) => (
                          <TaskItem
                            handleDescricaoClick={handleDescricaoClick}
                            columnsCard={columnsCardFinal}
                            provided={provided}
                            snapshot={snapshot}
                            task={task}
                            setTaskFantasma={setTaskFantasma}
                            taskFantasma={taskFantasma}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TaskList>
                )}
              </Droppable>
            ))}
        </DragDropContext>
      </div>
    </LoadingContainer>
  );
}

export default Kanban;
